import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiChip,
  MiModalConfirm,
  MiWorkListCard,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  PRINT_ORIENTATION,
} from "@miplatform/constants";
import {
  mdiPencilOutline,
  mdiPlus,
  mdiPrinter,
  mdiTrashCanOutline,
} from "@mdi/js";
import { usePrint } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";
import MaterialImage from "./components/MaterialImage";
import CloseoutMaterialPrintContent, {
  CloseoutMaterialMetadata,
  MaterialBadge,
  getMaterialSourceColor,
} from "./CloseoutMaterialPrintContent";
import { MaterialModal } from "./MaterialModal";
import { MATERIAL_SOURCES, MATERIAL_SOURCE_VALUES } from "./materialConstants";
import {
  applyMaterialChanges,
  useCloseoutMaterialViewModel,
} from "./useCloseoutMaterialViewModel";

const getMaterialSections = (materials, theme) => {
  const orderMaterials = materials.filter(
    (material) => material.source === MATERIAL_SOURCE_VALUES.ORDER
  );
  const restockMaterials = materials.filter(
    (material) => material.requiresReplacement
  );
  const usedMaterials = materials.filter((material) => material.isUsed);

  return [
    {
      key: "order",
      title: "Order Materials",
      materials: orderMaterials,
      isOrdered: true,
      badgeLabel: MATERIAL_SOURCES.ORDER.toUpperCase(),
      badgeColor: getMaterialSourceColor(MATERIAL_SOURCES.ORDER, theme),
    },
    {
      key: "restock",
      title: "Restock Materials",
      materials: restockMaterials,
      badgeLabel: MATERIAL_SOURCES.RESTOCK.toUpperCase(),
      badgeColor: getMaterialSourceColor(MATERIAL_SOURCES.RESTOCK, theme),
      showSource: false,
    },
    {
      key: "used",
      title: "Materials Used During Visit",
      materials: usedMaterials,
      showSource: false,
    },
  ];
};

const CloseoutMaterial = ({
  address,
  communityName,
  dispatcherName,
  materials = [],
  tenant,
  onPrint,
  isLoading = false,
  orderNumber,
  orderType,
  technicianName,
  performedOn,
  setIsMaterialChecked = () => {},
  issues = [],
  attemptId,
  assignmentId,
  getAttempt,
  readOnly = false,
}) => {
  const { classes, theme } = useStyles();

  const { handlePrintExtendedTemplate } = usePrint(tenant);
  const {
    closeMaterialModal,
    closeDeleteMaterialModal,
    deleteMaterial,
    isDeleteMaterialModalOpen,
    isLoading: isAddingMaterial,
    isMaterialModalOpen,
    materialChanges,
    materialPendingDelete,
    openAddMaterialModal,
    openDeleteMaterialModal,
    openEditMaterialModal,
    saveMaterial,
    selectedMaterial,
  } = useCloseoutMaterialViewModel({
    issues,
    attemptId,
    assignmentId,
    getAttempt,
    setIsMaterialChecked,
  });
  const materialList = useMemo(() => {
    const initialMaterials = Array.isArray(materials) ? materials : [];

    return applyMaterialChanges(initialMaterials, materialChanges);
  }, [materialChanges, materials]);
  const materialSections = getMaterialSections(materialList, theme);

  const handleMaterialsPrint = () => {
    setIsMaterialChecked(true);
    if (onPrint) {
      onPrint();
      return;
    }

    handlePrintExtendedTemplate(
      <CloseoutMaterialPrintContent
        address={address}
        communityName={communityName}
        dispatcherName={dispatcherName}
        materialSections={materialSections}
        technicianName={technicianName}
        performedOn={performedOn}
      />,
      {
        headerFields: [
          { label: "Order Type", value: orderType || "--" },
          { label: "Order #", value: orderNumber || "--" },
        ],
        orientation: PRINT_ORIENTATION.PORTRAIT,
      }
    );
  };

  return (
    <Box className={classes.root}>
      <Typography className={classes.title}>
        Print Materials <span className={classes.required}>*</span>
      </Typography>
      <Box style={{ position: "relative" }}>
        <CloseoutMaterialMetadata
          className={classes.metadata}
          technicianName={technicianName}
          performedOn={performedOn}
        />
        {!readOnly && (
          <MiButton
            onClick={openAddMaterialModal}
            variant={BUTTON_VARIANTS.OUTLINED}
            color="primary"
            style={{ position: "absolute", top: theme.spacing(2), right: 0 }}
            size="small"
            disabled={isLoading || isAddingMaterial || readOnly}
            startIcon={{ path: mdiPlus }}
          >
            Add Materials
          </MiButton>
        )}
      </Box>
      {materialSections.map((section) => (
        <MaterialSection
          key={section.key}
          {...section}
          onDeleteMaterial={openDeleteMaterialModal}
          onEditMaterial={openEditMaterialModal}
          readOnly={readOnly}
        />
      ))}
      {materialList.length === 0 && (
        <Typography className={classes.noMaterials}>
          No materials added yet.
        </Typography>
      )}
      {materialList.length > 0 && (
        <MiButton
          title="Print Materials"
          variant={BUTTON_VARIANTS.CONTAINED}
          color="primary"
          width="100%"
          disabled={isLoading || isAddingMaterial || materialList.length === 0}
          startIcon={{ path: mdiPrinter }}
          onClick={handleMaterialsPrint}
        />
      )}

      <MaterialModal
        open={isMaterialModalOpen}
        issues={issues}
        material={selectedMaterial}
        isLoading={isAddingMaterial}
        closeModal={closeMaterialModal}
        onSaveMaterial={saveMaterial}
      />

      <MiModalConfirm
        open={isDeleteMaterialModalOpen}
        title="Delete Material"
        titleIcon={{
          path: mdiTrashCanOutline,
          color: theme.palette.error.main,
        }}
        textPositive={BUTTON_LABELS.DELETE}
        buttonPositiveColor={BUTTON_COLORS.ERROR}
        description={`Are you sure you want to delete “${
          materialPendingDelete?.description ?? "this material"
        }”?`}
        loading={isAddingMaterial}
        handleNegative={closeDeleteMaterialModal}
        handlePositive={deleteMaterial}
      />
    </Box>
  );
};

const MaterialSection = ({
  title,
  materials,
  isOrdered = false,
  onDeleteMaterial,
  onEditMaterial,
  readOnly,
}) => {
  const { classes } = useStyles();

  if (materials.length === 0) {
    return null;
  }

  return (
    <Box className={classes.section}>
      <Typography className={classes.sectionTitle}>{title}</Typography>
      <Box className={classes.materialList}>
        {materials.map((material) => (
          <MaterialRow
            key={material.materialId}
            material={material}
            isRestocked={material.requiresReplacement}
            isOrdered={isOrdered}
            onDeleteMaterial={onDeleteMaterial}
            onEditMaterial={onEditMaterial}
            readOnly={readOnly}
          />
        ))}
      </Box>
    </Box>
  );
};

const MaterialRow = ({
  material,
  isRestocked = false,
  isOrdered = false,
  onDeleteMaterial,
  onEditMaterial,
  readOnly,
}) => {
  const { classes, theme } = useStyles();

  return (
    <MiWorkListCard
      item={material}
      cardContainerStyles={{
        dropShadow: "none",
        border: `1px solid ${theme.palette.grey.accent}`,
        boxShadow: "none",
        backgroundColor: theme.palette.grey.light,
      }}
      LeftComponent={MaterialImage}
      renderTitle={(item) => (
        <Box className={classes.materialContent}>
          <Typography className={classes.materialDescription}>
            {item.description}
          </Typography>
          <Box className={classes.chips}>
            <MiChip
              label={item.source}
              variant="outlined"
              size="small"
              className={classes.chip}
            />
            <MiChip
              label={`Qty: ${item.quantity}`}
              variant="outlined"
              size="small"
              className={classes.chip}
            />
            <MiChip
              label={item.brand}
              variant="outlined"
              size="small"
              className={classes.chip}
            />
          </Box>
        </Box>
      )}
      renderItemActions={() => (
        <>
          {isRestocked && (
            <MaterialBadge
              text={MATERIAL_SOURCES.RESTOCK.toUpperCase()}
              {...getMaterialSourceColor(MATERIAL_SOURCES.RESTOCK, theme)}
            />
          )}
          {isOrdered && (
            <MaterialBadge
              text={MATERIAL_SOURCES.ORDER.toUpperCase()}
              {...getMaterialSourceColor(MATERIAL_SOURCES.ORDER, theme)}
            />
          )}
        </>
      )}
      getMenuItems={() =>
        !readOnly && [
          {
            label: BUTTON_LABELS.EDIT,
            icon: mdiPencilOutline,
            action: () => onEditMaterial(material),
          },
          {
            label: BUTTON_LABELS.DELETE,
            icon: mdiTrashCanOutline,
            color: theme.palette.error.main,
            action: () => onDeleteMaterial(material),
          },
        ]
      }
    />
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.5,
  },
  required: {
    color: theme.palette.error.main,
  },
  metadata: {
    marginTop: theme.spacing(2),
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  materialList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  materialContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  chip: {
    marginRight: theme.spacing(1),
    borderRadius: 2,
    fontSize: 13,
    backgroundColor: theme.palette.grey.mediumLight,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 600,
  },
  materialDescription: {
    fontSize: 15,
    fontWeight: 400,
  },
  noMaterials: {
    padding: `${theme.spacing(4)} 0`,
    fontSize: 15,
    textAlign: "center",
  },
}));

export default CloseoutMaterial;
